import { useLayoutEffect, useEffect } from 'react';
import { Header } from '@components/header/Header';
import { MobileHeader } from '@components/header/mobileHeader/MobileHeader';
import {
  HeaderType,
  IHeaderAlertWrapper,
  getNavLinks,
  headerBgInitial,
  headerBgScrolled,
  paymentLinks,
} from '@components/header/Header.constants';
import { ThemeProvider, Box } from '@mui/material';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { Button } from '@components/button';
import { Link as RouterLink, Link as MuiLink, useLocation } from 'react-router-dom';
import { ApiEnvBanner } from '@/ApiEnvBanner';
import { MobileQuoteNavigation } from '@components/header/mobileHeader/MobileQuoteNavigation';
import composeTheme, { useComposeTheme } from '@theme/theme';
import { Background } from '@layout/Layout.constants';
import { AlertList } from '@components/alert';
import { useLayoutStore } from '@store/LayoutStore';
import { FontSize } from '@store/types/UserStoreSlice.type';
import { useGetBrokerage } from '@services/brokerage';
import { getTodaysHoliday } from '@theme/palettes/getPalette';
import { useApiEnvStore } from '@utils/api-client';
import { useGetScreenSize } from '@utils/useGetScreenSize';

export const HeaderWrapper = ({
  type,
  quote,
  actionItems,
  navTitle,
  buttons,
  hasUnsavedChanges,
  darkBackground = false,
  showNavLinks = true,
  disableActionItems = false,
  smallLogo = false,
}: IHeaderAlertWrapper) => {
  const themeToUse = useComposeTheme('dark', { fontSizeOverride: FontSize.DEFAULT });
  const { isLioSales, isInternalUser, isBroker, isCustomerUser, isCSR } = useStore((state) => state.userAuth, shallow);
  const currentUser = useStore((state) => state.currentUser, shallow);
  const { closeHeaderMenu } = useLayoutStore(
    (state) => ({
      closeHeaderMenu: state.closeHeaderMenu,
    }),
    shallow,
  );

  const [disableHolidayModeFlag] = useApiEnvStore((state) => [state.disableHolidayModeFlag], shallow);
  const [setPortalContext] = useStore((state) => [state.setPortalContext], shallow);

  const location = useLocation();
  const currentRoute = location.pathname;

  useEffect(() => {
    if (currentRoute.startsWith('/payments')) {
      setPortalContext(true);
    } else if (!currentRoute.startsWith('/settings')) {
      // settings and sub pages will not change your context
      setPortalContext(false);
    }

    // ensure menu is closed on route change
    closeHeaderMenu();
  }, [closeHeaderMenu, currentRoute, setPortalContext]);

  const { isMobileOrSmallScreen } = useGetScreenSize();

  useLayoutEffect(() => {
    if (!isMobileOrSmallScreen) {
      closeHeaderMenu();
    }
  }, [closeHeaderMenu, isMobileOrSmallScreen]);

  const submitAClaimButton = [
    <MuiLink key='claim-link' target='_blank' rel='noopener' to='https://lioinsurance.com/submit-a-claim/'>
      <Button variant='outlined'>Submit a Claim</Button>
    </MuiLink>,
  ];
  const getAQuoteButton = [
    <RouterLink
      key='get-quote-link'
      to='/new-quote'
      aria-disabled={isLioSales}
      style={{ pointerEvents: isLioSales ? 'none' : 'auto' }}
    >
      <Button variant='contained' disabled={isLioSales}>
        Get a Quote
      </Button>
    </RouterLink>,
  ];

  const currentBrokerageId = currentUser?.brokerageUsers?.[0]?.brokerageId;
  const { brokerage, isLoading: isBrokerageLoading } = useGetBrokerage({
    brokerageId: currentBrokerageId || '',
    isEnabled: !!currentBrokerageId && (isBroker || isCSR),
  });

  let buttonsToShow = buttons;
  let linksToShow = getNavLinks({
    isInternalUser,
    payerId: currentUser?.payer?.id,
    isTinyScreen: isMobileOrSmallScreen,
    isCustomerUser,
  })
    ?.filter((link) => !!link)
    ?.filter((link) =>
      link.isVisible === undefined
        ? true
        : isBrokerageLoading
          ? false
          : link.isVisible({ brokerageDivisionType: brokerage?.brokerageDivision?.type }),
    );

  let actionItemsToShow;
  let subLinkMenu;

  switch (type) {
    case HeaderType.Detail:
      subLinkMenu = quote && <MobileQuoteNavigation {...quote} />;
      linksToShow = [];
      actionItemsToShow = !isBroker ? actionItems : undefined;
      break;
    case HeaderType.Nav:
    case HeaderType.NavTransparent:
      buttonsToShow = [...submitAClaimButton, ...getAQuoteButton];
      if (isCustomerUser) {
        buttonsToShow = [...submitAClaimButton];
      }
      if (isInternalUser) {
        buttonsToShow = [...getAQuoteButton];
      }
      break;
    case HeaderType.NewQuote:
      linksToShow = [];
      break;
    case HeaderType.MakeAPayment:
      subLinkMenu = quote && <MobileQuoteNavigation {...quote} />;
      linksToShow = paymentLinks;
      actionItemsToShow = !isBroker ? actionItems : undefined;
      break;
    case HeaderType.Payments:
      subLinkMenu = quote && <MobileQuoteNavigation {...quote} />;
      linksToShow = paymentLinks;
      actionItemsToShow = !isBroker ? actionItems : undefined;

      const payerHasNoAccounts = currentUser?.payer?.accountTypes?.length === 0;
      buttonsToShow = isInternalUser
        ? []
        : [
            <RouterLink key='payment-link' to='/payments/new'>
              <Button variant='contained' disabled={isLioSales || payerHasNoAccounts}>
                Make a Payment
              </Button>
            </RouterLink>,
          ];

      break;
    case HeaderType.PlaceHolder:
      linksToShow = [];
      buttonsToShow = [];
      actionItemsToShow = undefined;
      break;
    case HeaderType.Settings:
      if (isInternalUser) {
        buttonsToShow = getAQuoteButton;
      }
      if (isBroker || isCSR) {
        buttonsToShow = [...submitAClaimButton, ...getAQuoteButton];
      }
      break;
    default:
      break;
  }

  const activeHoliday = getTodaysHoliday();
  // Easter keeps the standard theme, so don't override header color
  const overrideHeaderColor = activeHoliday && activeHoliday !== 'Easter' && !disableHolidayModeFlag;

  return (
    <ThemeProvider theme={themeToUse}>
      <ThemeProvider theme={composeTheme(darkBackground ? 'dark' : 'light')}>
        {/* EC: TODO/future really should be controlled in layout since it applies to the entire page */}
        <Background />
      </ThemeProvider>
      <div id='header-and-alerts' style={{ zIndex: 100, position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            width: '100%',
          }}
        >
          <AlertList />
        </div>

        <Box sx={{ top: 0, zIndex: 3 }}>
          {type !== HeaderType.PlaceHolder && <ApiEnvBanner />}
          {isMobileOrSmallScreen ? (
            <MobileHeader
              subLinkMenu={subLinkMenu}
              navLinks={linksToShow as any[]}
              type={type}
              initialBgColor={headerBgInitial({ darkBackground })[type]}
              quote={quote}
              title={navTitle}
              buttons={buttonsToShow}
              showNavLinks={linksToShow.length > 0}
            />
          ) : (
            <Header
              type={type}
              navLinks={linksToShow as any[]}
              initialBgColor={overrideHeaderColor ? 'background.main' : headerBgInitial({ darkBackground })[type]}
              scrolledBgColor={overrideHeaderColor ? 'background.main' : headerBgScrolled[type]}
              navTitle={navTitle}
              buttons={buttonsToShow}
              showNavLinks={showNavLinks}
              hasUnsavedChanges={hasUnsavedChanges}
              actionItems={actionItemsToShow}
              disableActionItems={disableActionItems}
              quote={quote}
              smallLogo={smallLogo}
            />
          )}
        </Box>
      </div>
    </ThemeProvider>
  );
};
